import { Box, CircularProgress, Link } from '@mui/material'
import React, { useEffect, useState } from 'react'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import GenericModal from '../../../components/GenericModal'
import CreateAndEditDistributionSeries from '../../DistributionSeriesAndPlan/Projects/CreateAndEditProject'
import { StyledDistributionPlansTableContainer, StyledGenericFlexContainer, StyledHeader, StyledText } from '../styles'

import { CreateAndEditDistributionPlan } from '../../DistributionSeriesAndPlan/DistributionPlan/CreateAndEditDistributionPlan'
import { type IDistributionSeries } from '../../../models/DistributionSeries'
import { useQuery } from '@tanstack/react-query'
import { DistributionPlansTable } from '../components/DistributionPlansTable'
import { WizardSelectionSearch } from '../components/WizardSelectionSearch'
import { type ISlot } from '../../../models/DistributionPlan'
import { getLocallyStoredDistributionSeriesId, getLocallyStoredNewlyAddedTerms, setLocallyStoredDistributionSeriesId, setLocallyStoredNewlyAddedTerms, useLoadingDots } from '../helper'
import theme from '../../../theme'
import { getDistributionSeriesBySeriesId, getSlotsForDistributionSeriesBySeriesId } from '../../../APIManager/DistributionManager'

interface SurveyProjectSetupPageProps {
  setIsLastPageSeeable: (isLastPageSeeable: boolean) => void
  setNewlyAddedTermsToProject: (newlyAddTermsToProject: string[]) => void
}

interface DistributionSeriesWithSlots {
  data: IDistributionSeries
  slotData: ISlot[]
}

export const SurveyProjectSetupPage: React.FC<SurveyProjectSetupPageProps> = ({ setIsLastPageSeeable, setNewlyAddedTermsToProject }): JSX.Element => {
  const loadingDotCount = useLoadingDots()
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState<boolean>(false)
  const [isSelectProjectModalOpen, setIsSelectProjectModalOpen] = useState<boolean>(false)
  const [isAddTermsModalOpen, setIsAddTermsModalOpen] = useState<boolean>(false)
  const [distributionSeriesId, setDistributionSeriesId] = useState<string | undefined>(getLocallyStoredDistributionSeriesId())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [triggerRerender, setTriggerRerender] = useState<number>(0)
  const [distributionSeries, setDistributionSeries] = useState<DistributionSeriesWithSlots | undefined>(undefined)
  const [newlyAddedTerms, setNewlyAddedTerms] = useState<string[]>(getLocallyStoredNewlyAddedTerms())
  const [isConfirmBackModalOpen, setIsConfirmBackModalOpen] = useState<boolean>(false)

  const { data: unused } = useQuery<number>({
    queryKey: ['termsByIds', triggerRerender, distributionSeriesId],
    queryFn: async () => {
      if (!distributionSeriesId) {
        return 1
      }
      setIsLoading(true)
      const distributionResponse = await getDistributionSeriesBySeriesId(distributionSeriesId)
      const slotResponse = await getSlotsForDistributionSeriesBySeriesId(distributionSeriesId)
      setIsLoading(false)
      setDistributionSeries({ data: distributionResponse.data, slotData: slotResponse.data })
      setLocallyStoredDistributionSeriesId(distributionResponse.data.distributionSeriesId)
      setIsLastPageSeeable(true)
      return 1
    }
  })

  // locally store newly added terms when updated and lift state to parent component
  useEffect(() => {
    setLocallyStoredNewlyAddedTerms(newlyAddedTerms)
    setNewlyAddedTermsToProject(newlyAddedTerms)
  }, [newlyAddedTerms])

  const handleBack = (): void => {
    setDistributionSeriesId(undefined)
    setLocallyStoredDistributionSeriesId(undefined)
    setNewlyAddedTerms([])
    setDistributionSeries(undefined)
    setIsConfirmBackModalOpen(false)
    setIsLastPageSeeable(false)
  }

  const handleDeleteSlot = (slotId: string): void => {
    setTriggerRerender(prev => prev + 1)
    if (newlyAddedTerms.includes(slotId)) {
      setNewlyAddedTerms(prev => prev.filter(termId => termId !== slotId))
    }
  }

  const handleAddSlot = (slotId: string): void => {
    setTriggerRerender(prev => prev + 1)
    if (!newlyAddedTerms.includes(slotId)) {
      setNewlyAddedTerms(prev => [...prev, slotId])
    }
  }

  let elementToRender: JSX.Element | null = null
  // loading screen if data is being fetched
  if (isLoading && !distributionSeries) {
    elementToRender =
      <StyledGenericFlexContainer gap={theme.spacing(2)}>
        <CircularProgress />
        Loading Data{'.'.repeat(loadingDotCount)}
      </StyledGenericFlexContainer>
  } else if (!distributionSeries) {
    // render option to create a distribution series if none exists
    elementToRender =
      <>
        <StyledHeader>Choose an existing survey project:</StyledHeader>
        <GenericButton
          label='Select Survey Project'
          variant='contained'
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Large}
          onClick={() => { setIsSelectProjectModalOpen(true) }}
        />
        <StyledHeader>Need to create one?</StyledHeader>
        <GenericButton
          label='Create Project'
          variant='contained'
          buttonType={ButtonType.Secondary}
          buttonSize={ButtonSize.Large}
          onClick={() => { setIsCreateProjectModalOpen(true) }}
        />
        <GenericModal
          isOpen={isCreateProjectModalOpen}
          maxWidth='sm'
          title='Create Survey Project'
          onClose={() => { setIsCreateProjectModalOpen(false) }}
          action={false}
        >
          <CreateAndEditDistributionSeries
            handleCloseModal={() => { setIsCreateProjectModalOpen(false) }}
            returnDistributionSeries={(series: IDistributionSeries) => {
              setDistributionSeriesId(series.distributionSeriesId)
              setLocallyStoredDistributionSeriesId(series.distributionSeriesId)
            }}
          />
        </GenericModal>
        <GenericModal
          isOpen={isSelectProjectModalOpen}
          maxWidth='sm'
          title='Select Survey Project'
          onClose={() => { setIsSelectProjectModalOpen(false) }}
          action={false}
        >
          <WizardSelectionSearch
            apiEndpoint='/api/distribution_series/'
            setValue={setDistributionSeriesId}
            setOpenModal={setIsSelectProjectModalOpen}
            label='Search for a Survey Project'
            labelField='title'
            name='distributionSeriesId'
          />
        </GenericModal>
      </>
  } else {
    // render option to add terms to the distribution series
    elementToRender =
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'flex-start' }}>
        <StyledHeader textAlign='start' pb={0}>
          You are setting up the survey project &quot;{distributionSeries.data.title}&quot;.
        </StyledHeader>
        <StyledText fontSize={theme.spacing(3.5)} textAlign='start'>
          Previously added terms are listed in the table below. Adding a new term will include it in the list.
        </StyledText>
        <StyledGenericFlexContainer sx={{ width: '100%' }}>
          <GenericButton
            label='Add Term'
            variant='contained'
            buttonType={ButtonType.Primary}
            buttonSize={ButtonSize.Large}
            onClick={() => { setIsAddTermsModalOpen(true) }}
          />
        </StyledGenericFlexContainer>
        <StyledDistributionPlansTableContainer>
          <DistributionPlansTable
            slotsData={distributionSeries.slotData}
            handleDeleteSlot={handleDeleteSlot}
          />
        </StyledDistributionPlansTableContainer>
        <Box sx={{ width: '100%', textAlign: 'flex-start', pl: theme.spacing(10), fontSize: theme.spacing(3.5) }}>
          Wrong project? <Link onClick={() => { setIsConfirmBackModalOpen(true) }}>Go back</Link>
        </Box>
        <GenericModal
          isOpen={isAddTermsModalOpen}
          title={'Create Term'}
          onClose={() => { setIsAddTermsModalOpen(false) }}
          showCloseIcon={false}
          maxWidth='sm'
          action={false}
        >
          <CreateAndEditDistributionPlan
            handleCloseModal={() => { setIsAddTermsModalOpen(false) }}
            handleAddSlot={handleAddSlot}
          />
        </GenericModal>
        <GenericModal
          isOpen={isConfirmBackModalOpen}
          title={`Exit "${distributionSeries.data.title}"?`}
          onClose={() => { setIsConfirmBackModalOpen(false) }}
          maxWidth='sm'
          action={true}
          confirmActionText='Confirm'
          confirmActionType={ButtonType.Destructive}
          onConfirm={async (): Promise<void> => { handleBack() }}
          destructiveButtonText='Cancel'
          destructiveButtonType={ButtonType.Secondary}
        >
          Are you sure you want to leave this survey project?
        </GenericModal>
      </Box>
  }

  return (
    <Box
      flexGrow='1'
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      overflow='hidden'
      sx={{
        height: '100%',
        maxHeight: '100%',
        width: '100%'
      }}
    >
      {elementToRender}
    </Box>
  )
}
